import { SupabaseClient } from '@supabase/supabase-js';
import { BaseRepository } from './base.repository';
import { ProjectUser, ProjectUserInsert, ProjectUserUpdate, ProjectUserRole } from '../entities/project-user';
import { PaginationOptions, PaginatedResponse } from '../types/pagination.types';
import { applyPagination } from '../utils/pagination.utils';

export class ProjectUserRepository extends BaseRepository<ProjectUser, ProjectUserInsert, ProjectUserUpdate> {
  constructor(supabase: SupabaseClient) {
    super(supabase, 'project_users');
  }

  /**
   * Save a ProjectUser entity (create or update based on dirty state)
   */
  async save(projectUser: ProjectUser): Promise<ProjectUser> {
    if (!projectUser.isDirty()) {
      return projectUser; // No changes to save
    }

    const data = projectUser.toData();
    const { id, created_at, joined_at, ...updateData } = data; // Remove fields that shouldn't be updated

    const { data: saved, error } = await this.supabase
      .from(this.tableName)
      .update(updateData)
      .eq('id', id)
      .select()
      .single();

    if (error) throw error;

    // Create new entity instance from saved data
    const savedProjectUser = ProjectUser.fromData(saved);
    savedProjectUser.markAsClean();
    return savedProjectUser;
  }

  /**
   * Override findById to return ProjectUser entity
   */
  async findById(id: string): Promise<ProjectUser | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('id', id)
      .single();

    if (error) {
      if (error.code === 'PGRST116') return null; // Not found
      throw error;
    }
    
    return data ? ProjectUser.fromData(data) : null;
  }

  /**
   * Override create to return ProjectUser entity
   */
  async create(data: ProjectUserInsert): Promise<ProjectUser> {
    const { data: created, error } = await this.supabase
      .from(this.tableName)
      .insert(data)
      .select()
      .single();

    if (error) throw error;
    return ProjectUser.fromData(created);
  }

  async findByProject(projectId: string): Promise<ProjectUser[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .order('joined_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(d => ProjectUser.fromData(d));
  }

  async findByProjectWithUsers(projectId: string): Promise<any[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select(`
        *,
        users:user_id (
          id,
          full_name,
          email,
          avatar_url
        )
      `)
      .eq('project_id', projectId)
      .order('joined_at', { ascending: true });

    if (error) throw error;
    return data || [];
  }

  async findByUser(userId: string): Promise<ProjectUser[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('user_id', userId)
      .order('joined_at', { ascending: false });

    if (error) throw error;
    return (data || []).map(d => ProjectUser.fromData(d));
  }

  async findByUserPaginated(userId: string, options?: PaginationOptions): Promise<PaginatedResponse<ProjectUser>> {
    const query = this.supabase
      .from(this.tableName)
      .select('*')
      .eq('user_id', userId);

    // Set default ordering if not specified
    const finalOptions = {
      ...options,
      orderBy: options?.orderBy || 'joined_at',
      order: options?.order || 'desc' as const
    };

    const result = await applyPagination<any, ProjectUser>(query, finalOptions, (d) => ProjectUser.fromData(d));
    
    // Ensure we always return PaginatedResponse
    if (!('pagination' in result)) {
      throw new Error('Expected paginated response');
    }
    
    return result;
  }

  async findByProjectAndUser(projectId: string, userId: string): Promise<ProjectUser | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .maybeSingle();

    if (error) throw error;
    return data ? ProjectUser.fromData(data) : null;
  }

  async updateRole(projectId: string, userId: string, role: ProjectUserRole): Promise<ProjectUser> {
    const projectUser = await this.findByProjectAndUser(projectId, userId);
    if (!projectUser) throw new Error('Project user not found');
    
    projectUser.updateRole(role);
    return this.save(projectUser);
  }

  async removeUser(projectId: string, userId: string): Promise<void> {
    const { error } = await this.supabase
      .from(this.tableName)
      .delete()
      .eq('project_id', projectId)
      .eq('user_id', userId);

    if (error) throw error;
  }

  async getProjectOwner(projectId: string): Promise<ProjectUser | null> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .eq('role', 'owner')
      .maybeSingle();

    if (error) throw error;
    return data ? ProjectUser.fromData(data) : null;
  }

  async getProjectAdmins(projectId: string): Promise<ProjectUser[]> {
    const { data, error } = await this.supabase
      .from(this.tableName)
      .select('*')
      .eq('project_id', projectId)
      .in('role', ['owner', 'admin'])
      .order('joined_at', { ascending: true });

    if (error) throw error;
    return (data || []).map(d => ProjectUser.fromData(d));
  }

  async hasPermission(projectId: string, userId: string, requiredRoles: ProjectUserRole[]): Promise<boolean> {
    const { count, error } = await this.supabase
      .from(this.tableName)
      .select('id', { count: 'exact', head: true })
      .eq('project_id', projectId)
      .eq('user_id', userId)
      .in('role', requiredRoles);

    if (error) throw error;
    return count !== null && count > 0;
  }

  /**
   * Alias for findByProjectAndUser for backward compatibility
   */
  async findByProjectIdAndUserId(projectId: string, userId: string): Promise<ProjectUser | null> {
    return this.findByProjectAndUser(projectId, userId);
  }
}